<template>
  <div
    class="min-h-full bg-slate-50/60 dark:bg-[#0B1120] text-slate-900 dark:text-[#F1F5F9] py-8 px-4 sm:px-6 lg:px-8 transition-colors"
  >
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :isOpen="confirmModal.isOpen"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :confirmText="confirmModal.confirmText"
      :isLoading="confirmModal.isLoading"
      @confirm="confirmModal.onConfirm"
      @cancel="closeModal"
    />
    <div class="mx-auto max-w-7xl space-y-6">
      <!-- Header -->
      <div class="bg-white dark:bg-[#111C30] p-6 rounded-3xl border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40 mb-2">
            Developer Sandbox
          </span>
          <h1 class="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white">
            Dummy Users Manager
          </h1>
          <p class="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            Provision and simulate test teachers and students across academic cohorts
          </p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <button
          type="button"
          @click="deleteAll"
          class="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-rose-600/10 hover:bg-rose-600/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800/40 font-semibold text-xs transition-colors"
        >
          Delete All Dummy Users
        </button>

        <button
          type="button"
          @click="createDummyTeachers"
          :disabled="loadingTeachers"
          class="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-xs hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm shadow-indigo-600/20"
        >
          <svg
            v-if="!loadingTeachers"
            class="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span
            v-else
            class="inline-block animate-spin mr-1.5"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          Add 5 Dummy Teachers
        </button>

        <button
          type="button"
          @click="createDummyStudents"
          :disabled="loadingStudents"
          class="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-xs hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm shadow-emerald-600/20"
        >
          <svg
            v-if="!loadingStudents"
            class="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span
            v-else
            class="inline-block animate-spin mr-1.5"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          Add 50 Dummy Students
        </button>

        <button
          @click="fetchDummyUsers"
          :disabled="loadingUsers"
          class="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-[#263449] font-semibold text-xs hover:bg-slate-200 dark:hover:bg-[#1F2E49] disabled:opacity-50 transition-colors"
        >
          <svg
            v-if="!loadingUsers"
            class="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span
            v-else
            class="inline-block animate-spin mr-1.5"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          Refresh Users
        </button>
      </div>

      <!-- Filter -->
      <div class="mb-6 flex items-center gap-3">
        <label
          for="role-filter"
          class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
          >Filter by Role:</label
        >
        <select
          id="role-filter"
          v-model="selectedRole"
          @change="fetchDummyUsers"
          class="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        >
          <option value="">All Users</option>
          <option value="teacher">Teachers Only</option>
          <option value="student">Students Only</option>
        </select>
      </div>

      <!-- Messages -->
      <div
        v-if="errorMessage"
        class="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-rose-700 dark:text-rose-300 text-xs font-medium"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="successMessage"
        class="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 text-emerald-700 dark:text-emerald-300 text-xs font-medium"
      >
        {{ successMessage }}
      </div>

      <!-- Users Grid -->
      <div
        v-if="dummyUsers.length === 0"
        class="text-center py-16 bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/90 dark:border-[#263449]"
      >
        <svg
          class="mx-auto h-10 w-10 text-slate-400 dark:text-slate-600 mb-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M17 20h5v-2a3 3 0 00-5.856-1.487M15 10a3 3 0 11-6 0 3 3 0 016 0zM6 20a3 3 0 003-3v-2a3 3 0 00-3-3H3a3 3 0 00-3 3v2a3 3 0 003 3h3z"
          />
        </svg>
        <h3 class="text-sm font-bold text-slate-900 dark:text-white">No dummy users found</h3>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
          Generate test records using the action buttons above.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="user in dummyUsers"
          :key="user._id"
          class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] shadow-xs overflow-hidden flex flex-col justify-between"
        >
          <!-- Header -->
          <div class="p-4 border-b border-slate-100 dark:border-[#263449]">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <!-- Avatar -->
                <div
                  :class="[
                    'w-10 h-10 rounded-xl text-white flex items-center justify-center text-sm font-bold shrink-0',
                    user.role === 'teacher'
                      ? 'bg-gradient-to-tr from-indigo-600 to-violet-600'
                      : 'bg-gradient-to-tr from-emerald-500 to-teal-600',
                  ]"
                >
                  {{ user.name ? user.name.charAt(0).toUpperCase() : "U" }}
                </div>

                <div class="min-w-0">
                  <h3 class="text-sm font-bold text-slate-900 dark:text-white truncate">
                    {{ user.name }}
                  </h3>
                  <p class="text-xs text-slate-400 dark:text-slate-500 truncate">
                    {{ user.phone || "No Phone" }}
                  </p>
                </div>
              </div>

              <!-- Role Badge -->
              <span
                :class="[
                  'px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider shrink-0',
                  user.role === 'teacher'
                    ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40'
                    : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/40',
                ]"
              >
                {{ user.role }}
              </span>
            </div>
          </div>

          <!-- Body -->
          <div class="p-4 space-y-2 text-xs">
            <div class="flex justify-between">
              <span class="text-slate-400 dark:text-slate-500">Phone:</span>
              <span class="font-medium text-slate-700 dark:text-slate-300">
                {{ user.phone || "N/A" }}
              </span>
            </div>

            <!-- Teacher Fields -->
            <div
              v-if="user.role === 'teacher'"
              class="flex justify-between"
            >
              <span class="text-slate-400 dark:text-slate-500">Subject:</span>
              <span class="font-medium text-slate-700 dark:text-slate-300">
                {{ user.subject || "N/A" }}
              </span>
            </div>

            <!-- Student Fields -->
            <div
              v-if="user.role === 'student'"
              class="flex justify-between"
            >
              <span class="text-slate-400 dark:text-slate-500">Roll Number:</span>
              <span class="font-medium text-slate-700 dark:text-slate-300">
                {{ user.rollNumber || "N/A" }}
              </span>
            </div>

            <div
              v-if="user.role === 'student'"
              class="flex justify-between"
            >
              <span class="text-slate-400 dark:text-slate-500">Enrollment:</span>
              <span class="font-medium text-slate-700 dark:text-slate-300">
                {{ formatDate(user.enrollmentDate) }}
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="px-4 py-3 bg-slate-50/50 dark:bg-[#18243A]/40 border-t border-slate-100 dark:border-[#263449] flex gap-2"
          >
            <!-- Show ONLY for Teacher -->
            <button
              v-if="user.role === 'teacher'"
              @click="impersonate(user._id)"
              :disabled="loadingImpersonate === user._id"
              class="flex-1 py-1.5 px-3 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 rounded-lg transition-colors border border-transparent hover:border-indigo-200 dark:hover:border-indigo-900/40 disabled:opacity-50"
            >
              {{
                loadingImpersonate === user._id ? "Loading..." : "Impersonate"
              }}
            </button>

            <!-- Delete (for both) -->
            <button
              @click="openDeleteModal(user._id, user.name)"
              class="flex-1 py-1.5 px-3 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors border border-transparent hover:border-rose-200 dark:hover:border-rose-900/40"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Impersonation Status Bar -->
      <div
        v-if="impersonatedUser"
        class="fixed bottom-0 left-0 right-0 bg-slate-900 text-white py-4 px-6 shadow-2xl"
      >
        <div
          class="mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <p class="text-base font-semibold">
            Currently impersonating:
            <span class="text-blue-400">{{ impersonatedUser }}</span>
          </p>
          <button
            @click="switchBack"
            :disabled="loadingSwitchBack"
            class="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 inline-flex items-center gap-2"
          >
            <svg
              v-if="!loadingSwitchBack"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 16V4m0 0L3 8m4-4l4 4"
              />
            </svg>
            <span
              v-else
              class="inline-block animate-spin"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
            Switch Back
          </button>
        </div>
      </div>

      <!-- Padding for fixed bar -->
      <div
        v-if="impersonatedUser"
        class="h-24"
      ></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted } from "vue";
  import jwtDecode from "jwt-decode";
  import ConfirmationModal from "@/components/ConfirmationModal.vue";

  import {
    addDummyTeachers,
    addDummyStudents,
    getDummyUsers,
    impersonateUser,
    switchBack as switchBackAPI,
    deleteAllDummyUsers,
    deleteUser,
  } from "@/services/salesService";

  const dummyUsers = ref([]);
  const selectedRole = ref("");
  const impersonatedUser = ref(false);

  const loadingTeachers = ref(false);
  const loadingStudents = ref(false);
  const loadingUsers = ref(false);
  const loadingImpersonate = ref(null);
  const loadingSwitchBack = ref(false);

  const errorMessage = ref("");
  const successMessage = ref("");

  // Confirmation Modal State
  const confirmModal = ref({
    isOpen: false,
    title: "Confirm Delete",
    message: "Are you sure you want to delete this user?",
    confirmText: "Delete",
    isLoading: false,
    userId: null,
    userName: "",
    onConfirm: null,
  });

  const clearMessages = () => {
    setTimeout(() => {
      errorMessage.value = "";
      successMessage.value = "";
    }, 5000);
  };

  const createDummyTeachers = async () => {
    loadingTeachers.value = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
      await addDummyTeachers();
      successMessage.value = "5 dummy teachers created successfully!";
      clearMessages();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message ||
        "Failed to create teachers. Please try again.";
      clearMessages();
    } finally {
      loadingTeachers.value = false;
    }
  };

  const createDummyStudents = async () => {
    loadingStudents.value = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
      await addDummyStudents();
      successMessage.value = "5 dummy students created successfully!";
      clearMessages();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message ||
        "Failed to create students. Please try again.";
      clearMessages();
    } finally {
      loadingStudents.value = false;
    }
  };

  const fetchDummyUsers = async () => {
    loadingUsers.value = true;
    errorMessage.value = "";

    try {
      const response = await getDummyUsers(selectedRole.value);
      dummyUsers.value = response.data.users || [];
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to fetch users.";
      dummyUsers.value = [];
    } finally {
      loadingUsers.value = false;
    }
  };

  const impersonate = async (userId) => {
    loadingImpersonate.value = userId;
    errorMessage.value = "";

    try {
      const response = await impersonateUser(userId);

      // 🔥 STORE NEW TOKEN
      localStorage.setItem("token", response.data.token);

      // Reload app to refresh role
      window.location.reload();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to impersonate user.";
      clearMessages();
    } finally {
      loadingImpersonate.value = null;
    }
  };

  const switchBack = async () => {
    loadingSwitchBack.value = true;
    errorMessage.value = "";

    try {
      const response = await switchBackAPI();

      localStorage.setItem("token", response.data.token);

      impersonatedUser.value = false;

      window.location.reload();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to switch back.";
    } finally {
      loadingSwitchBack.value = false;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  onMounted(() => {
    const token = localStorage.getItem("token");

    if (token) {
      try {
        const decoded = jwtDecode(token);

        if (decoded.impersonatedBy) {
          impersonatedUser.value = true;
        }
      } catch (err) {
        console.error("Invalid token");
      }
    }

    fetchDummyUsers();
  });
  const deleteAll = async () => {
    confirmModal.value = {
      isOpen: true,
      title: "Delete All Dummy Users",
      message:
        "Are you sure you want to delete all dummy users? This action cannot be undone.",
      confirmText: "Delete All",
      isLoading: false,
      userId: null,
      userName: "",
      onConfirm: confirmDeleteAll,
    };
  };

  const confirmDeleteAll = async () => {
    confirmModal.value.isLoading = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
      const response = await deleteAllDummyUsers();
      successMessage.value = response.data.message;
      closeModal();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to delete dummy users.";
      closeModal();
    }
  };

  const openDeleteModal = (userId, userName) => {
    confirmModal.value = {
      isOpen: true,
      title: "Delete User",
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      confirmText: "Delete",
      isLoading: false,
      userId: userId,
      userName: userName,
      onConfirm: confirmDeleteUser,
    };
  };

  const confirmDeleteUser = async () => {
    const userId = confirmModal.value.userId;
    confirmModal.value.isLoading = true;
    errorMessage.value = "";

    try {
      await deleteUser(userId);
      successMessage.value = `${confirmModal.value.userName} deleted successfully!`;
      closeModal();
      clearMessages();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to delete user.";
      closeModal();
      clearMessages();
    }
  };

  const closeModal = () => {
    confirmModal.value.isOpen = false;
    confirmModal.value.isLoading = false;
    confirmModal.value.userId = null;
    confirmModal.value.userName = "";
  };
</script>
